/**
 * TypeScript types for Purchase Order History (Bestilling Historikk) feature
 * Maps to backend POrderHistoryDto and related DTOs
 *
 * This feature provides hierarchical view of purchase orders with:
 * - Order header information
 * - Nested order lines
 * - Nested stock transactions for each line
 */

/**
 * Generic paginated result wrapper
 * Maps to BizClick.WMS.Shared.DTOs.PagedResult<T>
 */
export interface PagedResult<T> {
  /** The items in the current page */
  items: T[];

  /** Total number of records across all pages */
  totalRecords: number;

  /** Current page number (1-based) */
  pageNumber: number;

  /** Number of items per page */
  pageSize: number;

  /** Total number of pages */
  totalPages: number;

  /** Whether there is a previous page */
  hasPreviousPage: boolean;

  /** Whether there is a next page */
  hasNextPage: boolean;
}

/**
 * Purchase order history - hierarchical structure
 * Maps to BizClick.WMS.Shared.DTOs.POrderHistoryDto
 * Contains order with nested lines and transactions
 */
export interface POrderHistoryDto {
  // Order header information
  id: number;
  companyNo: string;
  orderNo: number;
  orderNoERP: string;
  orderStatus: number;
  orderStatusText: string;
  orderType?: string;
  orderDate?: string;
  deliveryDate?: string;
  supplierNo: string;
  supplierName?: string;
  supplierOrderNo?: string;
  yourRef?: string;
  ourRef?: string;
  deliveryMethod?: string;
  carrierName?: string;
  contactName?: string;
  warehouse?: string;
  projectNo?: string;
  depNo?: string;
  created?: string;
  lastUpdate?: string;
  createdBy?: string;
  updatedBy?: string;

  // Summary fields
  totalLines: number;
  totalQuantity: number;
  totalDelivered: number;
  totalTransactions: number;

  // Nested lines with transactions
  lines: POrderHistoryLineDto[];
}

/**
 * Purchase order line in history view
 * Maps to BizClick.WMS.Shared.DTOs.POrderHistoryLineDto
 * Contains line information with nested transactions
 */
export interface POrderHistoryLineDto {
  id: number;
  orderNo: number;
  lineNo: number;
  lineNoERP?: number;
  lineStatus: number;
  lineStatusText: string;
  itemNo: string;
  itemBarCode?: string;
  description1?: string;
  itemGroup1?: string;
  itemGroup2?: string;
  itemGroup3?: string;
  quantity?: number;
  quantityDelivered?: number;
  remainingQuantity: number;
  itemUnitCode?: string;
  itemUnitTxt?: string;
  price?: number;
  deliveryDate?: string;
  stdLocationName?: string;
  warehouse?: string;
  itemBatchNo?: string;
  batchItemYesNo?: boolean;
  serialNoItemYesNo?: boolean;
  created?: string;
  lastUpdate?: string;

  // Nested transactions for this line
  transactions: POrderHistoryTransactionDto[];
}

/**
 * Stock transaction in order history
 * Maps to BizClick.WMS.Shared.DTOs.POrderHistoryTransactionDto
 * Represents a single transaction related to an order line
 */
export interface POrderHistoryTransactionDto {
  id: number;
  lineID: number;
  lineType: number;
  lineTypeText: string;
  lineStatus: number;
  lineStatusText: string;
  orderNo: number;
  lineNo: number;
  itemNo: string;
  itemBarCode?: string;
  description1?: string;
  quantity?: number;
  itemUnitCode?: string;
  locationName?: string;
  toLocationName?: string;
  warehouse?: string;
  itemBatchNo?: string;
  itemBatchExpDate?: string;
  itemSerialNo?: string;
  transDt?: string;
  operatorID: string;
  batchID?: string;
  sscc?: string;
  created?: string;
}

/**
 * Query parameters for fetching purchase order history
 * Maps to BizClick.WMS.Shared.DTOs.POrderHistoryRequest
 * Used for pagination and filtering
 */
export interface POrderHistoryQueryParams {
  /** Start date for order date filter (inclusive) */
  fromDate?: Date | string;

  /** End date for order date filter (inclusive) */
  toDate?: Date | string;

  /** Page number (1-based, default: 1) */
  pageNumber?: number;

  /** Number of orders per page (default: 20, max: 100) */
  pageSize?: number;

  /** Optional filter by supplier number */
  supplierNo?: string;

  /** Optional filter by ERP order number (partial match) */
  orderNoERP?: string;
}

// POrderStatus/POrderLineStatus-enumene + getOrderStatusText/getLineStatusText fjernet 2026-07-02
// (WS3): status vises nå via StatusBadge/statusPresentation (Model A, master-cache). POrderStatus
// finnes kanonisk i types/porders.ts (importer derfra ved behov).

/**
 * Transaction line type enum (legacy-verdier fra BizClick Logistics)
 * 0=Varemottak, 1=Telling (fri), 2=Overføring, 3=Korreksjon, 4=Telling (forhåndsdefinert)
 */
export enum TransactionLineType {
  Varemottak = 0,              // Goods Receipt
  Telling = 1,                 // Stock Counting (free scan)
  Lageroverfoering = 2,        // Stock Transfer
  Korreksjon = 3,              // Stock Correction
  TellingForhaandsdefinert = 4  // Stock Counting (pre-defined list)
}

/**
 * Helper function to get transaction line type text
 */
export function getTransactionLineTypeText(lineType: number): string {
  switch (lineType) {
    case TransactionLineType.Varemottak:
      return 'Varemottak';
    case TransactionLineType.Telling:
      return 'Lagertelling';
    case TransactionLineType.Lageroverfoering:
      return 'Lageroverføring';
    case TransactionLineType.Korreksjon:
      return 'Lagerkorreksjon';
    case TransactionLineType.TellingForhaandsdefinert:
      return 'Lagertelling (forhåndsdefinert)';
    default:
      return `Type ${lineType}`;
  }
}

/**
 * Calculate completion percentage for an order
 */
export function calculateOrderCompletionPercentage(
  totalQuantity: number,
  totalDelivered: number
): number {
  if (totalQuantity === 0) return 0;
  return Math.min(Math.round((totalDelivered / totalQuantity) * 100), 100);
}

/**
 * Calculate completion percentage for an order line
 */
export function calculateLineCompletionPercentage(
  quantity: number | undefined,
  quantityDelivered: number | undefined
): number {
  if (!quantity || quantity === 0) return 0;
  const delivered = quantityDelivered || 0;
  return Math.min(Math.round((delivered / quantity) * 100), 100);
}

/**
 * Mottatt innkjøp for operatørens historikk (TW 36403717): innkjøpsordre med minst ett mottak,
 * valgt på siste mottaksdato. Fanen «Innkjøp» i Historikk-modulen.
 */
export interface ReceivedPurchaseOrderLineDto {
  orderNo: number;
  lineNo: number;
  itemNo: string;
  description1?: string | null;
  itemUnitTxt?: string | null;
  quantity?: number | null;
  quantityDelivered?: number | null;
  /** Lokasjon fra innkjøpsordren — der varen skal plasseres. */
  stdLocationName?: string | null;
  /** Lokasjon(er) varen faktisk ble mottatt på, kommaseparert. Tom i GO-mottak uten lokasjon. */
  receivedBinCodes?: string | null;
  lastReceiptDate?: string | null;
}

export interface ReceivedPurchaseOrderDto {
  orderNo: number;
  orderNoERP?: string | null;
  supplierNo?: string | null;
  supplierName?: string | null;
  purchaserRef?: string | null;
  warehouse?: string | null;
  lastReceiptDate?: string | null;
  lineCount: number;
  lines: ReceivedPurchaseOrderLineDto[];
}

export interface ReceivedPurchaseOrderQueryParams {
  fromDate?: string;
  toDate?: string;
  warehouse?: string;
  search?: string;
}
